import { z } from 'zod'

/** The six rides (D-050, PHASE3 T3.10). Order is the bots' draw order and the wire's id list. */
export const RIDE_IDS = [
  'jet_snail',
  'thundertoad',
  'wobblebot',
  'pufferloon',
  'drillmole',
  'crabwalker',
] as const
export type RideId = (typeof RIDE_IDS)[number]

const ms = z.number().int().positive()
const pct = z.number().min(0).max(300)
const speedPct = z.number().min(0).max(200)

/** One ride: its speed bonus and the one ability it brings (discriminated by `ability`). */
const dash = z.strictObject({
  speedPct,
  ability: z.literal('dash'),
  /** Slow strips and mud do not slow the rider. */
  ignoresSlow: z.boolean(),
})
const shockwave = z.strictObject({
  speedPct,
  ability: z.literal('shockwave'),
  /** Jump launch speed while riding, percent of the normal one. */
  jumpVelocityPct: pct,
  shockRadiusPx: z.number().int().positive(),
  shockStunMs: ms,
})
const bulldoze = z.strictObject({ speedPct, ability: z.literal('bulldoze'), bumpStunMs: ms })
const glide = z.strictObject({
  speedPct,
  ability: z.literal('glide'),
  /** Falling speed cap, percent of `maxFallSpeed`. */
  fallSpeedPct: z.number().min(1).max(100),
  glideLiftPxPerSec: z.number().positive(),
})
const burrow = z.strictObject({ speedPct, ability: z.literal('burrow'), burrowMs: ms })
const shell = z.strictObject({
  speedPct,
  ability: z.literal('shell'),
  blocksProjectiles: z.boolean(),
  bumpStunMs: ms,
})

/** Raw `rides.json` as authored (durations in ms). */
export const ridesFileSchema = z.strictObject({
  /** Shards that fill the meter (per runner, D-056). */
  chargeShards: z.number().int().positive(),
  rideDurationMs: ms,
  /** Wind-up between the summon press and the ride starting. */
  summonMs: z.number().int().min(0),
  dismountInvulnMs: z.number().int().min(0),
  /** Cap on all speed bonuses together while riding (ride + boost), percent over base. */
  maxSpeedPct: speedPct,
  /** Thundertoad's shockwave reaches runners whose feet are within this many px vertically. */
  shockTierPx: z.number().int().min(0),
  rides: z.strictObject({
    jet_snail: dash,
    thundertoad: shockwave,
    wobblebot: bulldoze,
    pufferloon: glide,
    drillmole: burrow,
    crabwalker: shell,
  }),
})

export type RidesFile = z.infer<typeof ridesFileSchema>
